import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/Card";
import { useToast } from "../hooks/useToast";
import { db } from "../lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { SUMMER_2026_SESSIONS, formatCurrency, validatePhone, validateEmail, HUBSTOPS as ALL_HUBSTOPS } from "../lib/utils";

interface Student {
  id: string;
  grade: string;
  needsCarSeat: boolean;
  needsBooster: boolean;
  needsAssistance: boolean;
}

interface GuardianInfo {
  fullName: string;
  email: string;
  phone: string;
}

// Hubstop options with pricing - map from utils.ts
const HUBSTOPS = ALL_HUBSTOPS.map(h => ({
  id: h.id,
  name: h.name,
  price: h.pricePerWeek
}));


// Multi-student discounts
const STUDENT_DISCOUNTS: Record<number, number> = {
  1: 1.0,
  2: 0.95,
  3: 0.90,
  4: 0.85,
  5: 0.80,
  6: 0.75,
};

export default function SummerCampQuote() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, loading: authLoading } = useAuth();
  const toast = useToast();

  const [guardianInfo, setGuardianInfo] = useState<GuardianInfo>({
    fullName: "",
    email: "",
    phone: "",
  });

  const [students, setStudents] = useState<Student[]>([
    {
      id: crypto.randomUUID(),
      grade: "",
      needsCarSeat: false,
      needsBooster: false,
      needsAssistance: false
    },
  ]);

  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [pickupAddress, setPickupAddress] = useState("");
  const [selectedHubstop, setSelectedHubstop] = useState("");
  const [message, setMessage] = useState("");
  const [timingPreference, setTimingPreference] = useState("");
  const [interestedInLateShuttle, setInterestedInLateShuttle] = useState(false);
  const [agreedToPrivacyPolicy, setAgreedToPrivacyPolicy] = useState(false);
  const [agreedToMarketing, setAgreedToMarketing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-select hubstop from URL parameter
  useEffect(() => {
    const hubstopParam = searchParams.get('hubstop');
    if (hubstopParam && HUBSTOPS.some(h => h.id === hubstopParam)) {
      setSelectedHubstop(hubstopParam);
    }
  }, [searchParams]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth?redirect=/summer-camp-quote");
    }
  }, [user, authLoading, navigate]);

  // Auto-fill guardian info from user profile if available
  useEffect(() => {
    if (user?.displayName) {
      setGuardianInfo(prev => ({
        ...prev,
        fullName: prev.fullName || user.displayName || "",
      }));
    }
    if (user?.email) {
      setGuardianInfo(prev => ({
        ...prev,
        email: prev.email || user.email || "",
      }));
    }
  }, [user]);

  // Calculate pricing
  const calculatePricing = () => {
    const weekCount = selectedWeeks.length;
    const studentCount = students.filter((s) => s.grade).length;

    if (weekCount === 0 || studentCount === 0 || !selectedHubstop) {
      return null;
    }

    const hubstop = HUBSTOPS.find(h => h.id === selectedHubstop);
    if (!hubstop || hubstop.price === 0) {
      return null;
    }

    const pricePerWeek = hubstop.price;
    const discount = STUDENT_DISCOUNTS[studentCount] || 0.75;
    const discountPercent = Math.round((1 - discount) * 100);
    const perWeekWithDiscount = pricePerWeek * discount;
    const totalPrice = perWeekWithDiscount * weekCount * studentCount;

    return {
      perWeekPerStudent: pricePerWeek,
      perWeekWithDiscount,
      weekCount,
      studentCount,
      discountPercent,
      totalPrice,
      hubstopName: hubstop.name,
    };
  };

  const pricing = calculatePricing();

  // Get schedule info based on hubstop selection
  const getScheduleInfo = () => {
    const hubstop = HUBSTOPS.find(h => h.id === selectedHubstop);
    if (!hubstop) return null;

    if (hubstop.price === 165) {
      return {
        dropoff: "8:30 AM",
        pickup: "3:30 PM",
      };
    } else if (hubstop.price === 75) {
      return {
        dropoff: "7:15 AM",
        pickup: "3:45 PM",
      };
    }
    return null;
  };

  const scheduleInfo = getScheduleInfo();

  // Week selection handlers
  const toggleWeek = (weekNumber: number) => {
    setSelectedWeeks((prev) =>
      prev.includes(weekNumber)
        ? prev.filter((w) => w !== weekNumber)
        : [...prev, weekNumber]
    );
  };

  const selectAllWeeks = () => {
    setSelectedWeeks(SUMMER_2026_SESSIONS.map((_, i) => i + 1));
  };

  const clearWeeks = () => {
    setSelectedWeeks([]);
  };

  // Student management
  const addStudent = () => {
    if (students.length >= 6) {
      toast.warning("Maximum students reached", "You can add up to 6 students per quote.");
      return;
    }
    setStudents([...students, {
      id: crypto.randomUUID(),
      grade: "",
      needsCarSeat: false,
      needsBooster: false,
      needsAssistance: false
    }]);
  };

  const removeStudent = (id: string) => {
    if (students.length <= 1) return;
    setStudents(students.filter((s) => s.id !== id));
  };

  const updateStudent = (id: string, field: keyof Student, value: string | boolean) => {
    setStudents(students.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  // Form validation
  const validateForm = () => {
    if (!guardianInfo.fullName) {
      toast.error("Missing information", "Please enter your full name.");
      return false;
    }
    if (!validateEmail(guardianInfo.email)) {
      toast.error("Invalid email", "Please enter a valid email address.");
      return false;
    }
    if (!validatePhone(guardianInfo.phone)) {
      toast.error("Invalid phone", "Please enter a valid phone number.");
      return false;
    }
    if (!pickupAddress) {
      toast.error("Missing address", "Please enter your pickup address.");
      return false;
    }
    if (!selectedHubstop) {
      toast.error("No hubstop selected", "Please select a hubstop location.");
      return false;
    }
    const validStudents = students.filter((s) => s.grade);
    if (validStudents.length === 0) {
      toast.error("Missing students", "Please add at least one student.");
      return false;
    }
    if (selectedWeeks.length === 0) {
      toast.error("No weeks selected", "Please select at least one week.");
      return false;
    }
    if (!agreedToPrivacyPolicy) {
      toast.error("Privacy Policy", "You must agree to the privacy policy to continue.");
      return false;
    }
    return true;
  };

  // Submit quote request
  const handleSubmit = async () => {
    if (!validateForm() || !pricing) return;

    setIsSubmitting(true);

    try {
      const validStudents = students.filter((s) => s.grade);

      await addDoc(collection(db, "quoteRequests"), {
        userId: user?.uid,
        quoteType: "summer-camp",
        guardianInfo,
        students: validStudents,
        pickupAddress,
        selectedHubstop: HUBSTOPS.find(h => h.id === selectedHubstop)?.name,
        dropoffAddress: "Summerside Camps at SCH, 8000 Cherokee St, Philadelphia, PA 19118",
        selectedWeeks,
        message: message || "",
        timingPreference: timingPreference || "",
        interestedInLateShuttle,
        agreedToPrivacyPolicy,
        agreedToMarketing,
        scheduleInfo,
        pricing,
        campInfo: {
          name: "Summerside Camps at SCH",
          address: "8000 Cherokee St, Philadelphia, PA 19118",
          pricePerWeek: pricing.perWeekPerStudent,
        },
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Send to Zapier for Google Sheets and email
      try {
        const hubstop = HUBSTOPS.find(h => h.id === selectedHubstop);
        const zapierPayload = {
          timestamp: new Date().toISOString(),
          quoteType: "summer-camp",
          status: "pending",
          guardianName: guardianInfo.fullName,
          guardianEmail: guardianInfo.email,
          guardianPhone: guardianInfo.phone,
          studentCount: validStudents.length,
          camperDetails: validStudents.map((s, i) =>
            `Camper ${i + 1}: Grade ${s.grade}${s.needsCarSeat ? ', Car Seat' : ''}${s.needsBooster ? ', Booster' : ''}${s.needsAssistance ? ', Needs Assistance' : ''}`
          ).join(" | "),
          pickupAddress: pickupAddress,
          dropoffAddress: "Summerside Camps at SCH, 8000 Cherokee St, Philadelphia, PA 19118",
          hubstopName: hubstop?.name || "",
          selectedWeeks: selectedWeeks.sort((a, b) => a - b).join(", "),
          weekCount: selectedWeeks.length,
          distanceMiles: 0, // Not calculated for summer camp quotes
          durationMinutes: 0, // Not calculated for summer camp quotes
          pricePerStudentPerWeek: pricing.perWeekWithDiscount,
          totalPerStudent: pricing.perWeekWithDiscount * selectedWeeks.length,
          totalRoutePerWeek: pricing.perWeekWithDiscount * validStudents.length,
          totalPrice: pricing.totalPrice,
          discountPercent: pricing.discountPercent || 0,
          message: message || "",
          timingPreference: timingPreference || "",
          interestedInLateShuttle: interestedInLateShuttle ? "Yes" : "No",
          agreedToPrivacyPolicy: agreedToPrivacyPolicy ? "Yes" : "No",
          agreedToMarketing: agreedToMarketing ? "Yes" : "No",
          userReferralCode: (profile as any)?.referralCode || "",
          referredBy: (profile as any)?.referredBy || "",
        };

        console.log("Sending to Zapier webhook:", zapierPayload);
        const response = await fetch("https://hooks.zapier.com/hooks/catch/22972432/uwwui7o/", {
          method: "POST",
          body: JSON.stringify(zapierPayload),
        });
        console.log("Zapier webhook response:", response.status, response.statusText);
        const responseData = await response.json();
        console.log("Zapier webhook response data:", responseData);
      } catch (zapierError) {
        // Don't fail the submission if Zapier fails
        console.error("Zapier webhook error:", zapierError);
      }

      setSubmitted(true);
      toast.success("Quote submitted!", "Check your email for your personalized quote.");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Submission failed", "Please try again or contact support.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-shuttlebee-yellow"></div>
      </div>
    );
  }

  if (submitted) {
    const validStudents = students.filter((s) => s.grade);
    const hubstop = HUBSTOPS.find(h => h.id === selectedHubstop);

    return (
      <div className="min-h-screen bg-gradient-to-br from-shuttlebee-dark via-shuttlebee-navy to-shuttlebee-blue flex items-center justify-center p-4">
        <Card className="max-w-3xl w-full">
          <CardContent className="pt-8 pb-8">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">✅</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Quote Submitted!</h1>
              <p className="text-gray-600">
                We've sent your personalized quote to <strong>{guardianInfo.email}</strong>
              </p>
            </div>

            {/* Quote Summary */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Quote Summary</h2>

              {/* Guardian Information */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Guardian Information</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>Name:</strong> {guardianInfo.fullName}</p>
                  <p><strong>Email:</strong> {guardianInfo.email}</p>
                  <p><strong>Phone:</strong> {guardianInfo.phone}</p>
                </div>
              </div>

              {/* Campers */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Campers ({validStudents.length})</h3>
                <div className="space-y-2">
                  {validStudents.map((student, idx) => (
                    <div key={student.id} className="text-sm text-gray-700 bg-white rounded p-3">
                      <p className="font-medium">Camper {idx + 1}</p>
                      <p>Grade: {student.grade}</p>
                      {(student.needsCarSeat || student.needsBooster || student.needsAssistance) && (
                        <p className="text-gray-600 mt-1">
                          {student.needsCarSeat && '🚗 Car Seat '}
                          {student.needsBooster && '💺 Booster '}
                          {student.needsAssistance && '🤝 Assistance Needed'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Transportation Details */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Transportation Details</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>Pickup Location:</strong> {hubstop?.name}</p>
                  <p><strong>Destination:</strong> Summerside Camps at SCH</p>
                  <p className="text-xs text-gray-600">8000 Cherokee St, Philadelphia, PA 19118</p>
                </div>
              </div>

              {/* Weeks Selected */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Weeks Selected ({selectedWeeks.length} weeks)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedWeeks.sort((a, b) => a - b).map((weekNum) => {
                    const session = SUMMER_2026_SESSIONS.find(s => s.value === weekNum);
                    return (
                      <div key={weekNum} className="bg-white rounded px-3 py-2 text-sm text-gray-700 border border-gray-200">
                        {session ? session.label : `Week ${weekNum}`}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              {message && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Additional Message</h3>
                  <div className="bg-white rounded p-3 text-sm text-gray-700 whitespace-pre-wrap">
                    {message}
                  </div>
                </div>
              )}

              {/* Additional Options */}
              {interestedInLateShuttle && (
                <div className="mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                    <p className="text-blue-900">🕐 Interested in late shuttle option</p>
                  </div>
                </div>
              )}

              {/* Pricing */}
              {pricing && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Pricing</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Price per week per camper:</span>
                      <span className="font-medium">{formatCurrency(pricing.perWeekPerStudent)}</span>
                    </div>
                    {pricing.discountPercent > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Multi-camper discount ({pricing.discountPercent}% off):</span>
                        <span className="font-medium">
                          -{formatCurrency((pricing.perWeekPerStudent - pricing.perWeekWithDiscount) * validStudents.length * selectedWeeks.length)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-700">Number of weeks:</span>
                      <span className="font-medium">{selectedWeeks.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Number of campers:</span>
                      <span className="font-medium">{validStudents.length}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-shuttlebee-dark">{formatCurrency(pricing.totalPrice)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">📧 What's Next?</h3>
              <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside">
                <li>Check your email for a detailed quote confirmation</li>
                <li>We'll review your request and send you payment information</li>
                <li>Visit your dashboard to track the status of your quote</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button onClick={() => navigate("/dashboard")} className="w-full">
                View in Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} className="w-full">
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-500 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1 bg-white/20 rounded-full text-sm font-medium mb-4">
            🏕️ Summer 2026
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Summerside Camps at SCH Transportation
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Safe, reliable daily transportation to Summerside Camps at SCH.
            Fixed pricing, flexible weeks, small group minivans.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Instruction Note */}
        <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>📋 Note:</strong> Please complete all sections to view your quote and request seats on the shuttle.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Camp Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">🏕️</span>
                  Camp Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900">Summerside Camps at SCH</h3>
                  <p className="text-green-800 text-sm mt-1">
                    8000 Cherokee St, Philadelphia, PA 19118
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Guardian Information */}
            <Card>
              <CardHeader>
                <CardTitle>Guardian Information</CardTitle>
                <CardDescription>Primary contact for this registration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  label="Full Name"
                  value={guardianInfo.fullName}
                  onChange={(e) => setGuardianInfo({ ...guardianInfo, fullName: e.target.value })}
                  placeholder="John Doe"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="Email"
                    type="email"
                    value={guardianInfo.email}
                    onChange={(e) => setGuardianInfo({ ...guardianInfo, email: e.target.value })}
                    placeholder="you@example.com"
                  />
                  <Input
                    label="Phone"
                    type="tel"
                    value={guardianInfo.phone}
                    onChange={(e) => setGuardianInfo({ ...guardianInfo, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Hubstop Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Pickup Location</CardTitle>
                <CardDescription>
                  Where should we pick up your camper(s)?
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-2">
                  <p>
                    Pickup locations are determined based on the addresses of registered participants to create safe, efficient routes.
                  </p>
                  <p>
                    All pickup points must be legal parking areas or designated loading zones where vehicles can safely stop without blocking traffic or violating local regulations.
                  </p>
                  <p>
                    Final pickup locations and times will be confirmed once enrollment is complete and routes are set.
                  </p>
                </div>

                <Input
                  label="Home Address"
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  placeholder="123 Main St, Philadelphia, PA 19103"
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Hubstop
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {HUBSTOPS.filter(h => h.id !== "custom").map((hubstop) => {
                      const isSelected = selectedHubstop === hubstop.id;
                      return (
                        <button
                          key={hubstop.id}
                          onClick={() => setSelectedHubstop(hubstop.id)}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            isSelected
                              ? "border-green-500 bg-green-50"
                              : "border-gray-200 hover:border-green-300 hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{hubstop.name}</span>
                            {isSelected && <span className="text-green-500">✓</span>}
                          </div>
                          {hubstop.price > 0 && (
                            <p className="text-sm text-gray-600 mt-1">
                              ${hubstop.price}/week per camper, roundtrip
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => navigate("/custom-quote")}
                    className="mt-3 w-full p-4 rounded-lg border-2 border-blue-500 bg-blue-50 hover:bg-blue-100 hover:border-blue-600 text-left transition-all shadow-sm"
                  >
                    <span className="font-semibold text-blue-900">✨ Request Custom Hubspot</span>
                    <p className="text-xs text-blue-700 mt-1">Get a personalized route quote</p>
                  </button>
                </div>

                {scheduleInfo && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">Schedule for Selected Hubstop</h4>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Morning Drop-off: {scheduleInfo.dropoff}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Afternoon Pickup: {scheduleInfo.pickup}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Students */}
            <Card>
              <CardHeader>
                <CardTitle>Campers</CardTitle>
                <CardDescription>Add all children attending camp (up to 6)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {students.map((student, index) => (
                  <div key={student.id} className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">Camper {index + 1}</span>
                      {students.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeStudent(student.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <Input
                      label="Grade"
                      placeholder="e.g., 3rd, 4th, 5th"
                      value={student.grade}
                      onChange={(e) => updateStudent(student.id, "grade", e.target.value)}
                    />

                    <div className="space-y-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={student.needsCarSeat}
                          onChange={(e) => updateStudent(student.id, "needsCarSeat", e.target.checked)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Needs car seat</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={student.needsBooster}
                          onChange={(e) => updateStudent(student.id, "needsBooster", e.target.checked)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Needs booster seat</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={student.needsAssistance}
                          onChange={(e) => updateStudent(student.id, "needsAssistance", e.target.checked)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Needs additional assistance</span>
                      </label>
                    </div>
                  </div>
                ))}

                {students.length < 6 && (
                  <Button variant="outline" onClick={addStudent} className="w-full">
                    + Add Another Camper
                  </Button>
                )}

                {students.length > 1 && (
                  <p className="text-sm text-green-600 font-medium">
                    🎉 Multi-camper discount: {STUDENT_DISCOUNTS[students.length] ?
                      `${Math.round((1 - STUDENT_DISCOUNTS[students.length]) * 100)}% off` :
                      "25% off"}!
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Week Selection */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Select Camp Weeks</CardTitle>
                    <CardDescription>
                      Summer 2026 • 10 weeks available
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllWeeks}>
                      Select All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={clearWeeks}>
                      Clear
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SUMMER_2026_SESSIONS.map((session, index) => {
                    const weekNumber = index + 1;
                    const isSelected = selectedWeeks.includes(weekNumber);
                    const hasClosedNotice = weekNumber === 2 || weekNumber === 4;

                    return (
                      <button
                        key={weekNumber}
                        onClick={() => toggleWeek(weekNumber)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-green-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900">
                            Week {weekNumber}
                          </span>
                          {isSelected && <span className="text-green-500">✓</span>}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {session.dates}
                          {hasClosedNotice && (
                            <span className="text-blue-600 ml-1">
                              • Closed {weekNumber === 2 ? "June 19th" : "July 3rd"}
                            </span>
                          )}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Late Shuttle Option */}
            <Card>
              <CardContent className="pt-6">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={interestedInLateShuttle}
                    onChange={(e) => setInterestedInLateShuttle(e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">
                      If a 4:30 PM shuttle becomes available, would you select this option?
                    </span>
                    <p className="text-sm text-gray-600 mt-1">
                      We're gauging interest in a later pickup time to accommodate families who need extended care.
                    </p>
                  </div>
                </label>
              </CardContent>
            </Card>

            {/* Timing & Pickup Preferences */}
            <Card>
              <CardHeader>
                <CardTitle>Timing & Pickup Preferences</CardTitle>
                <CardDescription>
                  Help us plan your route — any constraints or preferences are welcome.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <textarea
                  rows={4}
                  value={timingPreference}
                  onChange={(e) => setTimingPreference(e.target.value)}
                  placeholder="e.g. Need pickup no earlier than 8:00 AM — school drop-off runs late. Prefer the corner of X and Y St rather than our driveway. Can't do Fridays week 3."
                  className="w-full rounded-lg border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm px-3 py-2"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Examples: earliest/latest acceptable pickup time, preferred corner or landmark, days with conflicts, sibling coordination needs.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <Card className="border-2 border-green-200 bg-gradient-to-br from-white to-green-50">
                <CardHeader className="pb-4">
                  <CardTitle className="text-center">Price Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  {pricing ? (
                    <div className="space-y-4">
                      <div className="text-center pb-4 border-b">
                        <p className="text-sm text-gray-500">Total Price</p>
                        <p className="text-4xl font-bold text-green-600">
                          {formatCurrency(pricing.totalPrice)}
                        </p>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Hubstop</span>
                          <span className="font-medium">{pricing.hubstopName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Base rate</span>
                          <span className="font-medium">{formatCurrency(pricing.perWeekPerStudent)}/week per camper, roundtrip</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Campers</span>
                          <span className="font-medium">{pricing.studentCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Weeks selected</span>
                          <span className="font-medium">{pricing.weekCount}</span>
                        </div>
                        {pricing.discountPercent > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>Multi-camper discount</span>
                            <span className="font-medium">-{pricing.discountPercent}%</span>
                          </div>
                        )}
                      </div>

                      <div className="bg-green-100 rounded-lg p-3 text-center">
                        <p className="text-sm text-green-800">
                          <span className="font-semibold">{formatCurrency(pricing.perWeekWithDiscount)}</span>
                          /week per camper, roundtrip
                        </p>
                      </div>

                      {/* Message Field */}
                      <div className="pt-4">
                        <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                          Additional Message or Special Requests (Optional)
                        </label>
                        <textarea
                          id="message"
                          rows={4}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          placeholder="Any special requests, questions, or additional information you'd like us to know..."
                          className="w-full rounded-lg border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 text-sm px-3 py-2"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          E.g., specific pickup time preferences, sibling coordination, accessibility needs, etc.
                        </p>
                      </div>

                      <div className="space-y-3 pt-2 border-t border-gray-200">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={agreedToPrivacyPolicy}
                            onChange={(e) => setAgreedToPrivacyPolicy(e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-700">
                            I agree to the{" "}
                            <a
                              href="/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:underline font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Privacy Policy
                            </a>
                            {" "}and{" "}
                            <a
                              href="/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:underline font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Terms of Service
                            </a>{" "}
                            <span className="text-red-500">*</span>
                          </span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={agreedToMarketing}
                            onChange={(e) => setAgreedToMarketing(e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-700">
                            I agree to receive marketing communications
                          </span>
                        </label>
                      </div>

                      <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !agreedToPrivacyPolicy}
                        loading={isSubmitting}
                        className="w-full bg-green-600 hover:bg-green-700"
                        size="lg"
                      >
                        Submit Route Request
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        You'll receive your quote via email within minutes
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-5xl mb-4">🏕️</p>
                      <p>Select hubstop, weeks, and add campers to see pricing</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* What's Included */}
              <Card className="mt-4">
                <CardContent className="pt-6">
                  <h4 className="font-semibold text-gray-900 mb-4">What's Included</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      Round-trip daily transportation
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      Minivans and Passenger Vans
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      Background-checked drivers
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      Real-time GPS tracking
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      Direct driver communication
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-500">✓</span>
                      Flexible week-by-week booking
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
